import {
  ActionIcon,
  Badge,
  Button,
  Checkbox,
  Divider,
  Grid,
  Group,
  Modal,
  Paper,
  PasswordInput,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useForm } from '@mantine/form';
import { useMedplum } from '@medplum/react-hooks';
import type { Extension, Practitioner } from '@medplum/fhirtypes';
import { UnifyDateInput } from '@/components/UnifyDateInput';
import { formatDateOnlyLocal, parseDateOnlyLocal, toFhirDateOnlyLocal } from '@/lib/dateHelpers';
import { zod4Resolver } from 'mantine-form-zod-resolver';
import { IconCalendarEvent, IconPlus, IconTrash } from '@tabler/icons-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  boardCertModalSchema,
  deaModalSchema,
  educationModalSchema,
  licenseModalSchema,
  licensesEducationFieldLabels,
  licensesEducationSchema,
  supplementalModalSchema,
  trainingModalSchema,
  type BoardCertModalFormValues,
  type DeaModalFormValues,
  type EducationModalFormValues,
  type LicenseModalFormValues,
  type LicensesEducationFormValues,
  type SupplementalModalFormValues,
  type TrainingModalFormValues,
} from '@/schemas/practitioner.schema';
import {
  CAQH_EXT_URLS,
  CREDENTIAL_TYPE_SYSTEM,
  QUALIFICATION_EXT_URLS,
} from '@unify/fhir-model/constants';
import {
  getCaqhAttestationDate,
  getCaqhId,
  getCaqhPassword,
  getCaqhUsername,
  setCaqhAttestationDate as setCaqhAttestationDateExtension,
  setCaqhId as setCaqhIdExtension,
  setCaqhPassword as setCaqhPasswordExtension,
  setCaqhUsername as setCaqhUsernameExtension,
} from '@unify/fhir-model/resources/practitioner';
import { LICENSE_TYPES, US_STATES, TRAINING_TYPE_OPTIONS } from '../constants';
import { missingFieldMessage } from '../../../lib/formValidation';
import { OnboardingSection } from '../OnboardingSection';
import { useOnboardingPractitioner } from '../useOnboardingPractitioner';
import { completeProviderOnboardingSection } from '../../../lib/providerOnboardingService';

const BOARD_CERT_TYPE_OPTIONS = [
  'Board Certified',
  'Board Eligible',
  'Subspecialty',
  'Other',
];

const SPECIALTY_TRAINING_TYPE_OPTIONS = [
  'Fellowship',
  'Residency',
  'Postdoctoral',
  'Continuing Education',
  'Other',
];

const LICENSE_STATUS_OPTIONS = [
  { value: 'Active', label: 'Active' },
  { value: 'Inactive', label: 'Inactive' },
  { value: 'Pending', label: 'Pending' },
  { value: 'Expired', label: 'Expired' },
  { value: 'Superseded', label: 'Superseded' },
];

const TRAINING_SELECT_OPTIONS = TRAINING_TYPE_OPTIONS.map((t) => ({ value: t, label: t }));
const MANAGED_QUALIFICATION_CODES = ['license', 'board-certification', 'dea', 'supplemental'] as const;

function formatDateForDisplay(dateStr: string | null): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
}

type Qualification = NonNullable<Practitioner['qualification']>[number];

function getQualificationCode(qualification: Qualification): string | undefined {
  return qualification.code?.coding?.find((coding) => coding.system === CREDENTIAL_TYPE_SYSTEM)?.code;
}

function getQualificationExtension(qualification: Qualification, url: string): Extension | undefined {
  return qualification.extension?.find((extension) => extension.url === url);
}

function getQualificationString(qualification: Qualification, url: string): string | undefined {
  const extension = getQualificationExtension(qualification, url);
  return extension?.valueString ?? extension?.valueCode;
}

function getQualificationBoolean(qualification: Qualification, url: string): boolean | undefined {
  return getQualificationExtension(qualification, url)?.valueBoolean;
}

function getEditableIdentifierValue(qualification: Qualification): string {
  return (
    qualification.identifier?.find(
      (identifier) =>
        identifier.system !== 'https://unify.health/fhir/salesforce-id' &&
        identifier.system !== 'https://unify.health/fhir/verifiable-id',
    )?.value ?? ''
  );
}

function toIsoDate(value?: string): string | undefined {
  if (!value || value === '—') return undefined;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value.trim())) return value.trim();
  const parsed = parseDateOnlyLocal(value.trim());
  if (parsed) return formatDateOnlyLocal(parsed);
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return undefined;
  return toFhirDateOnlyLocal(date);
}

function toIsoDateTime(value?: string): string | undefined {
  if (!value || value === '—') return undefined;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return undefined;
  return date.toISOString();
}

function removePractitionerExtension(resource: Practitioner, url: string): Practitioner {
  return {
    ...resource,
    extension: (resource.extension ?? []).filter((extension) => extension.url !== url),
  };
}

const EMPTY_LICENSE_MODAL: LicenseModalFormValues = {
  licenseName: '',
  licenseNumber: '',
  jurisdictionState: null,
  licenseStatus: '',
  issueDate: null,
  expirationDate: null,
  licenseType: null,
  isPrimary: false,
  superseded: false,
  documentFile: null,
};

const EMPTY_CERT_MODAL: BoardCertModalFormValues = {
  boardCertificationName: '',
  certType: null,
  certificationNumber: '',
  certificationDate: null,
  certificateFile: null,
};

const EMPTY_DEA_MODAL: DeaModalFormValues = {
  deaNumberName: '',
  active: true,
  expirationDate: null,
  drugSchedules: '',
  businessActivity: '',
};

const EMPTY_SUPP_MODAL: SupplementalModalFormValues = {
  trainingName: '',
  completedDate: null,
  trainingType: null,
};

const EMPTY_TRAINING_MODAL: TrainingModalFormValues = {
  trainingType: '',
  completedDate: null,
};

const EMPTY_EDU_MODAL: EducationModalFormValues = {
  institution: '',
  degree: '',
  graduationYear: '',
};

export function LicensesEducationSection(): JSX.Element {
  const medplum = useMedplum();
  const profile = useOnboardingPractitioner();
  const navigate = useNavigate();
  const certFileInputRef = useRef<HTMLInputElement>(null);
  const licenseDocInputRef = useRef<HTMLInputElement>(null);
  const hydratedProfileVersionRef = useRef<string | undefined>(undefined);
  const [saving, setSaving] = useState(false);

  const [eduModalOpen, setEduModalOpen] = useState(false);
  const [editingEduId, setEditingEduId] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [certModalOpen, setCertModalOpen] = useState(false);
  const [editingCertId, setEditingCertId] = useState<string | null>(null);
  const [deaModalOpen, setDeaModalOpen] = useState(false);
  const [editingDeaId, setEditingDeaId] = useState<string | null>(null);
  const [suppModalOpen, setSuppModalOpen] = useState(false);
  const [editingSuppId, setEditingSuppId] = useState<string | null>(null);
  const [trainingModalOpen, setTrainingModalOpen] = useState(false);

  const form = useForm<LicensesEducationFormValues>({
    initialValues: {
      caqhUsername: '',
      caqhPassword: '',
      caqhId: '',
      caqhAttestationDate: null,
      educationEntries: [],
      licenses: [],
      certifications: [],
      deaCredentials: [],
      specialtyTraining: [],
      trainings: [],
    },
    validate: zod4Resolver(licensesEducationSchema),
  });

  const educationModalForm = useForm<EducationModalFormValues>({
    initialValues: EMPTY_EDU_MODAL,
    validate: zod4Resolver(educationModalSchema),
  });

  const licenseModalForm = useForm<LicenseModalFormValues>({
    initialValues: EMPTY_LICENSE_MODAL,
    validate: zod4Resolver(licenseModalSchema),
  });

  const boardCertModalForm = useForm<BoardCertModalFormValues>({
    initialValues: EMPTY_CERT_MODAL,
    validate: zod4Resolver(boardCertModalSchema),
  });

  const deaModalForm = useForm<DeaModalFormValues>({
    initialValues: EMPTY_DEA_MODAL,
    validate: zod4Resolver(deaModalSchema),
  });

  const supplementalModalForm = useForm<SupplementalModalFormValues>({
    initialValues: EMPTY_SUPP_MODAL,
    validate: zod4Resolver(supplementalModalSchema),
  });

  const trainingModalForm = useForm<TrainingModalFormValues>({
    initialValues: EMPTY_TRAINING_MODAL,
    validate: zod4Resolver(trainingModalSchema),
  });

  useEffect(() => {
    if (!profile || profile.resourceType !== 'Practitioner') {
      return;
    }

    const hydrationKey = `${profile.id ?? 'unknown'}:${profile.meta?.versionId ?? profile.meta?.lastUpdated ?? 'initial'}`;
    if (hydratedProfileVersionRef.current === hydrationKey) {
      return;
    }
    hydratedProfileVersionRef.current = hydrationKey;

    const qualifications = profile.qualification ?? [];

    form.setValues({
      caqhUsername: getCaqhUsername(profile) ?? '',
      caqhPassword: getCaqhPassword(profile) ?? '',
      caqhId: getCaqhId(profile) ?? '',
      caqhAttestationDate: getCaqhAttestationDate(profile) ?? null,
      educationEntries: [],
      licenses: qualifications
        .filter((qualification) => getQualificationCode(qualification) === 'license')
        .map((qualification, index) => ({
          id: qualification.id ?? `license-${index}`,
          jurisdictionState: getQualificationString(qualification, QUALIFICATION_EXT_URLS.jurisdictionState) ?? null,
          licenseName: qualification.code?.text ?? '',
          licenseNumber: getEditableIdentifierValue(qualification),
          expirationDate: qualification.period?.end ? (toIsoDate(qualification.period.end) ?? null) : null,
          licenseStatus: getQualificationString(qualification, QUALIFICATION_EXT_URLS.licenseStatus) ?? '',
          issueDate: qualification.period?.start ? (toIsoDate(qualification.period.start) ?? null) : null,
          licenseType: getQualificationString(qualification, QUALIFICATION_EXT_URLS.licenseType) ?? null,
          documentFileName: undefined,
          isPrimary: getQualificationBoolean(qualification, QUALIFICATION_EXT_URLS.isPrimary) ?? false,
          superseded: getQualificationBoolean(qualification, QUALIFICATION_EXT_URLS.superseded) ?? false,
        })),
      certifications: qualifications
        .filter((qualification) => getQualificationCode(qualification) === 'board-certification')
        .map((qualification, index) => ({
          id: qualification.id ?? `board-cert-${index}`,
          boardCertificationName: qualification.code?.text ?? '',
          certificationNumber: getEditableIdentifierValue(qualification),
          certificationDate: qualification.period?.start
            ? (toIsoDate(qualification.period.start) ?? null)
            : null,
          certificateFileName: '',
          certType: getQualificationString(qualification, QUALIFICATION_EXT_URLS.boardCertificationType) ?? null,
        })),
      deaCredentials: qualifications
        .filter((qualification) => getQualificationCode(qualification) === 'dea')
        .map((qualification, index) => ({
          id: qualification.id ?? `dea-${index}`,
          deaNumberName: qualification.code?.text ?? '',
          active:
            (getQualificationString(qualification, QUALIFICATION_EXT_URLS.licenseStatus) ?? 'active') !== 'inactive',
          expirationDate: qualification.period?.end ? (toIsoDate(qualification.period.end) ?? null) : null,
          drugSchedules: getQualificationString(qualification, QUALIFICATION_EXT_URLS.drugSchedules) ?? '',
          businessActivity: getQualificationString(qualification, QUALIFICATION_EXT_URLS.businessActivity) ?? '',
        })),
      specialtyTraining: qualifications
        .filter((qualification) => getQualificationCode(qualification) === 'supplemental')
        .map((qualification, index) => ({
          id: qualification.id ?? `supp-${index}`,
          trainingName: qualification.code?.text ?? '',
          completedDate: qualification.period?.start ? (toIsoDate(qualification.period.start) ?? null) : null,
          trainingType: getQualificationString(qualification, QUALIFICATION_EXT_URLS.trainingType) ?? null,
        })),
      trainings: [],
    });
  }, [profile]);

  const openEduAdd = (): void => {
    setEditingEduId(null);
    educationModalForm.setValues(EMPTY_EDU_MODAL);
    setEduModalOpen(true);
  };

  const openEduEdit = (edu: LicensesEducationFormValues['educationEntries'][number]): void => {
    setEditingEduId(edu.id);
    educationModalForm.setValues({
      institution: edu.institution,
      degree: edu.degree,
      graduationYear: edu.graduationYear,
    });
    setEduModalOpen(true);
  };

  const saveEducation = educationModalForm.onSubmit((values) => {
    const entry = {
      id: editingEduId ?? String(Date.now()),
      institution: values.institution,
      degree: values.degree,
      graduationYear: values.graduationYear,
    };
    if (editingEduId) {
      const index = form.values.educationEntries.findIndex((e) => e.id === editingEduId);
      if (index >= 0) {
        form.setFieldValue(`educationEntries.${index}`, entry);
      }
    } else {
      form.insertListItem('educationEntries', entry);
    }
    setEduModalOpen(false);
    setEditingEduId(null);
  });

  const removeEducation = (id: string, e: React.MouseEvent): void => {
    e.stopPropagation();
    const index = form.values.educationEntries.findIndex((ed) => ed.id === id);
    if (index >= 0) {
      form.removeListItem('educationEntries', index);
    }
  };

  const openLicenseAdd = (): void => {
    setEditingId(null);
    licenseModalForm.setValues(EMPTY_LICENSE_MODAL);
    setShowModal(true);
  };

  const openLicenseEdit = (license: LicensesEducationFormValues['licenses'][number]): void => {
    setEditingId(license.id);
    licenseModalForm.setValues({
      licenseName: license.licenseName,
      licenseNumber: license.licenseNumber,
      jurisdictionState: license.jurisdictionState,
      licenseStatus: license.licenseStatus || '',
      issueDate: license.issueDate,
      expirationDate: license.expirationDate,
      licenseType: license.licenseType,
      isPrimary: license.isPrimary,
      superseded: license.superseded,
      documentFile: null,
    });
    setShowModal(true);
  };

  const closeLicenseModal = (): void => {
    setShowModal(false);
    setEditingId(null);
  };

  const saveLicense = licenseModalForm.onSubmit((values) => {
    const entry: LicensesEducationFormValues['licenses'][number] = {
      id: editingId ?? String(Date.now()),
      licenseName: values.licenseName.trim(),
      licenseNumber: values.licenseNumber,
      jurisdictionState: values.jurisdictionState,
      licenseStatus: values.licenseStatus || 'Active',
      issueDate: values.issueDate,
      expirationDate: values.expirationDate,
      licenseType: values.licenseType,
      isPrimary: values.isPrimary,
      superseded: values.superseded,
      documentFileName: values.documentFile?.name,
    };
    if (editingId) {
      const index = form.values.licenses.findIndex((lic) => lic.id === editingId);
      if (index >= 0) {
        const existing = form.values.licenses[index];
        form.setFieldValue(`licenses.${index}`, {
          ...entry,
          documentFileName: values.documentFile?.name ?? existing.documentFileName,
        });
      }
    } else {
      form.insertListItem('licenses', entry);
    }
    closeLicenseModal();
  });

  const removeLicense = (id: string, e: React.MouseEvent): void => {
    e.stopPropagation();
    const index = form.values.licenses.findIndex((lic) => lic.id === id);
    if (index >= 0) {
      form.removeListItem('licenses', index);
    }
  };

  const openCertAdd = (): void => {
    setEditingCertId(null);
    boardCertModalForm.setValues(EMPTY_CERT_MODAL);
    setCertModalOpen(true);
  };

  const openCertEdit = (cert: LicensesEducationFormValues['certifications'][number]): void => {
    setEditingCertId(cert.id);
    boardCertModalForm.setValues({
      boardCertificationName: cert.boardCertificationName,
      certificationNumber: cert.certificationNumber,
      certificationDate: cert.certificationDate,
      certType: cert.certType,
      certificateFile: null,
    });
    setCertModalOpen(true);
  };

  const saveCertificate = boardCertModalForm.onSubmit((values) => {
    const entry: LicensesEducationFormValues['certifications'][number] = {
      id: editingCertId ?? String(Date.now()),
      boardCertificationName: values.boardCertificationName.trim(),
      certificationNumber: values.certificationNumber,
      certificationDate: values.certificationDate,
      certType: values.certType,
      certificateFileName: values.certificateFile?.name ?? '',
    };
    if (editingCertId) {
      const index = form.values.certifications.findIndex((c) => c.id === editingCertId);
      if (index >= 0) {
        const existing = form.values.certifications[index];
        form.setFieldValue(`certifications.${index}`, {
          ...entry,
          certificateFileName: values.certificateFile?.name ?? existing.certificateFileName ?? '',
        });
      }
    } else {
      form.insertListItem('certifications', entry);
    }
    setCertModalOpen(false);
    setEditingCertId(null);
  });

  const removeCertificate = (id: string, e: React.MouseEvent): void => {
    e.stopPropagation();
    const index = form.values.certifications.findIndex((c) => c.id === id);
    if (index >= 0) {
      form.removeListItem('certifications', index);
    }
  };

  const openDeaAdd = (): void => {
    setEditingDeaId(null);
    deaModalForm.setValues(EMPTY_DEA_MODAL);
    setDeaModalOpen(true);
  };

  const openDeaEdit = (dea: LicensesEducationFormValues['deaCredentials'][number]): void => {
    setEditingDeaId(dea.id);
    deaModalForm.setValues({
      deaNumberName: dea.deaNumberName,
      active: dea.active,
      expirationDate: dea.expirationDate,
      drugSchedules: dea.drugSchedules,
      businessActivity: dea.businessActivity,
    });
    setDeaModalOpen(true);
  };

  const saveDea = deaModalForm.onSubmit((values) => {
    const entry: LicensesEducationFormValues['deaCredentials'][number] = {
      id: editingDeaId ?? String(Date.now()),
      deaNumberName: values.deaNumberName.trim(),
      active: values.active,
      expirationDate: values.expirationDate,
      drugSchedules: values.drugSchedules,
      businessActivity: values.businessActivity,
    };
    if (editingDeaId) {
      const index = form.values.deaCredentials.findIndex((d) => d.id === editingDeaId);
      if (index >= 0) {
        form.setFieldValue(`deaCredentials.${index}`, entry);
      }
    } else {
      form.insertListItem('deaCredentials', entry);
    }
    setDeaModalOpen(false);
    setEditingDeaId(null);
  });

  const removeDea = (id: string, e: React.MouseEvent): void => {
    e.stopPropagation();
    const index = form.values.deaCredentials.findIndex((d) => d.id === id);
    if (index >= 0) {
      form.removeListItem('deaCredentials', index);
    }
  };

  const openSuppAdd = (): void => {
    setEditingSuppId(null);
    supplementalModalForm.setValues(EMPTY_SUPP_MODAL);
    setSuppModalOpen(true);
  };

  const openSuppEdit = (cred: LicensesEducationFormValues['specialtyTraining'][number]): void => {
    setEditingSuppId(cred.id);
    supplementalModalForm.setValues({
      trainingName: cred.trainingName,
      completedDate: cred.completedDate,
      trainingType: cred.trainingType,
    });
    setSuppModalOpen(true);
  };

  const saveSupp = supplementalModalForm.onSubmit((values) => {
    const entry: LicensesEducationFormValues['specialtyTraining'][number] = {
      id: editingSuppId ?? String(Date.now()),
      trainingName: values.trainingName.trim(),
      completedDate: values.completedDate,
      trainingType: values.trainingType,
    };
    if (editingSuppId) {
      const index = form.values.specialtyTraining.findIndex((c) => c.id === editingSuppId);
      if (index >= 0) {
        form.setFieldValue(`specialtyTraining.${index}`, entry);
      }
    } else {
      form.insertListItem('specialtyTraining', entry);
    }
    setSuppModalOpen(false);
    setEditingSuppId(null);
  });

  const removeSupp = (id: string, e: React.MouseEvent): void => {
    e.stopPropagation();
    const index = form.values.specialtyTraining.findIndex((c) => c.id === id);
    if (index >= 0) {
      form.removeListItem('specialtyTraining', index);
    }
  };

  const openTrainingAdd = (): void => {
    trainingModalForm.setValues(EMPTY_TRAINING_MODAL);
    setTrainingModalOpen(true);
  };

  const saveTraining = trainingModalForm.onSubmit((values) => {
    form.insertListItem('trainings', {
      id: String(Date.now()),
      trainingType: values.trainingType,
      completedDate: values.completedDate ?? '',
    });
    setTrainingModalOpen(false);
  });

  const removeTraining = (id: string, e: React.MouseEvent): void => {
    e.stopPropagation();
    const index = form.values.trainings.findIndex((t) => t.id === id);
    if (index >= 0) {
      form.removeListItem('trainings', index);
    }
  };

  const handleSave = form.onSubmit(
    async (values) => {
      if (!profile || profile.resourceType !== 'Practitioner') {
        return;
      }

      setSaving(true);
      try {
        const preservedQualifications = (profile.qualification ?? []).filter((qualification) => {
          const code = getQualificationCode(qualification);
          return !code || !MANAGED_QUALIFICATION_CODES.includes(code as (typeof MANAGED_QUALIFICATION_CODES)[number]);
        });

        let updated: Practitioner = {
          ...profile,
          qualification: [
            ...preservedQualifications,
            ...values.licenses.map((license) => ({
              code: {
                text: license.licenseName,
                coding: [{ system: CREDENTIAL_TYPE_SYSTEM, code: 'license', display: 'License' }],
              },
              identifier: license.licenseNumber ? [{ value: license.licenseNumber }] : undefined,
              period: {
                start: license.issueDate ? toIsoDateTime(license.issueDate) : undefined,
                end: license.expirationDate ? toIsoDate(license.expirationDate) : undefined,
              },
              extension: [
                ...(license.jurisdictionState
                  ? [{ url: QUALIFICATION_EXT_URLS.jurisdictionState, valueCode: license.jurisdictionState } satisfies Extension]
                  : []),
                ...(license.licenseStatus
                  ? [{ url: QUALIFICATION_EXT_URLS.licenseStatus, valueCode: license.licenseStatus } satisfies Extension]
                  : []),
                ...(license.licenseType
                  ? [{ url: QUALIFICATION_EXT_URLS.licenseType, valueString: license.licenseType } satisfies Extension]
                  : []),
                ...(license.isPrimary
                  ? [{ url: QUALIFICATION_EXT_URLS.isPrimary, valueBoolean: true } satisfies Extension]
                  : []),
                ...(license.superseded
                  ? [{ url: QUALIFICATION_EXT_URLS.superseded, valueBoolean: true } satisfies Extension]
                  : []),
              ],
            })),
            ...values.certifications.map((certificate) => ({
              code: {
                text: certificate.boardCertificationName,
                coding: [{ system: CREDENTIAL_TYPE_SYSTEM, code: 'board-certification', display: 'Board Certification' }],
              },
              identifier: certificate.certificationNumber ? [{ value: certificate.certificationNumber }] : undefined,
              extension: [
                ...(certificate.certType
                  ? [{ url: QUALIFICATION_EXT_URLS.boardCertificationType, valueCode: certificate.certType } satisfies Extension]
                  : []),
              ],
            })),
            ...values.deaCredentials.map((dea) => ({
              code: {
                text: dea.deaNumberName,
                coding: [{ system: CREDENTIAL_TYPE_SYSTEM, code: 'dea', display: 'DEA' }],
              },
              period: {
                end: dea.expirationDate ? toIsoDate(dea.expirationDate) : undefined,
              },
              extension: [
                {
                  url: QUALIFICATION_EXT_URLS.licenseStatus,
                  valueCode: dea.active ? 'active' : 'inactive',
                } satisfies Extension,
                ...(dea.drugSchedules
                  ? [{ url: QUALIFICATION_EXT_URLS.drugSchedules, valueString: dea.drugSchedules } satisfies Extension]
                  : []),
                ...(dea.businessActivity
                  ? [{ url: QUALIFICATION_EXT_URLS.businessActivity, valueString: dea.businessActivity } satisfies Extension]
                  : []),
              ],
            })),
            ...values.specialtyTraining.map((credential) => ({
              code: {
                text: credential.trainingName,
                coding: [{ system: CREDENTIAL_TYPE_SYSTEM, code: 'supplemental', display: 'Supplemental' }],
              },
              period: {
                start: credential.completedDate ? toIsoDate(credential.completedDate) : undefined,
              },
              extension: [
                ...(credential.trainingType
                  ? [{ url: QUALIFICATION_EXT_URLS.trainingType, valueCode: credential.trainingType } satisfies Extension]
                  : []),
              ],
            })),
          ],
        };

        updated = setCaqhUsernameExtension(updated, values.caqhUsername.trim());
        updated = setCaqhPasswordExtension(updated, values.caqhPassword.trim());
        updated = setCaqhIdExtension(updated, values.caqhId.trim());
        updated = values.caqhAttestationDate
          ? setCaqhAttestationDateExtension(updated, values.caqhAttestationDate)
          : removePractitionerExtension(updated, CAQH_EXT_URLS.attestationDate);

        await medplum.updateResource(updated);
        const sectionResult = await completeProviderOnboardingSection(medplum, 'licenses-education');
        if (!sectionResult.ok) {
          const detail = sectionResult.missingSections
            .map((s) => `${s.title}: ${s.missingFields.join(', ')}`)
            .join('; ');
          notifications.show({
            color: 'red',
            title: 'Could not verify onboarding',
            message: detail || sectionResult.message,
          });
          return;
        }

        medplum.invalidateSearches('Practitioner');
        medplum.invalidateSearches('Task');
        notifications.show({
          color: 'green',
          title: 'Licenses saved',
          message: 'Your licenses and credentials have been saved successfully.',
        });
        navigate('/onboarding');
      } catch (error) {
        notifications.show({
          color: 'red',
          title: 'Could not save licenses',
          message: error instanceof Error ? error.message : 'Please review your entries and try again.',
        });
      } finally {
        setSaving(false);
      }
    },
    () => {
      notifications.show({
        color: 'red',
        title: 'Missing required fields',
        message: missingFieldMessage(Object.keys(form.errors), licensesEducationFieldLabels),
      });
    },
  );

  return (
    <OnboardingSection title="Licenses and Education" onSave={() => handleSave()} saveLabel="Save" isSaving={saving}>
      <Stack gap="xl">
        <Text size="sm" c="dimmed">
          Add your licenses, board certifications, and credentialing information.
        </Text>

        {/* ===== Education ===== */}
        <div>
          <Group justify="space-between" mb="xs">
            <Text size="sm" fw={600} c="gray.8">
              Education
            </Text>
            <Button leftSection={<IconPlus size={16} />} variant="default" size="xs" onClick={openEduAdd}>
              Add Education
            </Button>
          </Group>
          <Table highlightOnHover withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Institution</Table.Th>
                <Table.Th>Degree</Table.Th>
                <Table.Th>Graduation Year</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {form.values.educationEntries.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={4}>
                    <Text c="dimmed" size="sm" ta="center" py="lg">
                      No education entries yet. Click Add Education to add one.
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ) : (
                form.values.educationEntries.map((edu) => (
                  <Table.Tr
                    key={edu.id}
                    style={{ cursor: 'pointer' }}
                    onClick={() => openEduEdit(edu)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openEduEdit(edu);
                      }
                    }}
                  >
                    <Table.Td><Text size="sm">{edu.institution || '—'}</Text></Table.Td>
                    <Table.Td><Text size="sm">{edu.degree || '—'}</Text></Table.Td>
                    <Table.Td><Text size="sm">{edu.graduationYear || '—'}</Text></Table.Td>
                    <Table.Td onClick={(e) => e.stopPropagation()}>
                      <ActionIcon variant="subtle" color="red" aria-label="Delete education" onClick={(e) => removeEducation(edu.id, e)}>
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Table.Td>
                  </Table.Tr>
                ))
              )}
            </Table.Tbody>
          </Table>
        </div>

        <Divider />

        {/* ===== Professional Licenses ===== */}
        <div>
          <Group justify="space-between" mb="xs">
            <Text size="sm" fw={600} c="gray.8">
              Professional licenses
            </Text>
            <Button leftSection={<IconPlus size={16} />} variant="default" size="xs" onClick={openLicenseAdd}>
              Add License
            </Button>
          </Group>
          <Table highlightOnHover withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Jurisdiction State</Table.Th>
                <Table.Th>License Name</Table.Th>
                <Table.Th>License Number</Table.Th>
                <Table.Th>Expiration Date</Table.Th>
                <Table.Th>License Status</Table.Th>
                <Table.Th>Document</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {form.values.licenses.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={7}>
                    <Text c="dimmed" size="sm" ta="center" py="lg">
                      No licenses yet. Click Add License to add one.
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ) : (
                form.values.licenses.map((license) => (
                  <Table.Tr
                    key={license.id}
                    style={{ cursor: 'pointer' }}
                    onClick={() => openLicenseEdit(license)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openLicenseEdit(license);
                      }
                    }}
                  >
                    <Table.Td><Text size="sm">{license.jurisdictionState || '—'}</Text></Table.Td>
                    <Table.Td><Text size="sm">{license.licenseName}</Text></Table.Td>
                    <Table.Td><Text size="sm">{license.licenseNumber || '—'}</Text></Table.Td>
                    <Table.Td>
                      <Text size="sm">
                        {license.expirationDate ? formatDateForDisplay(license.expirationDate) : '—'}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge color={license.licenseStatus === 'Active' ? 'green' : 'gray'} size="sm" variant="light">
                        {license.licenseStatus}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c={license.documentFileName ? undefined : 'dimmed'}>
                        {license.documentFileName || '—'}
                      </Text>
                    </Table.Td>
                    <Table.Td onClick={(e) => e.stopPropagation()}>
                      <ActionIcon variant="subtle" color="red" aria-label="Delete license" onClick={(e) => removeLicense(license.id, e)}>
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Table.Td>
                  </Table.Tr>
                ))
              )}
            </Table.Tbody>
          </Table>
        </div>

        {/* ===== Board Certifications ===== */}
        <div>
          <Group justify="space-between" mb="xs">
            <Text size="sm" fw={600} c="gray.8">
              Board certifications
            </Text>
            <Button leftSection={<IconPlus size={16} />} variant="default" size="xs" onClick={openCertAdd}>
              Add Certificate
            </Button>
          </Group>
          <Table highlightOnHover withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Certification Name</Table.Th>
                <Table.Th>Number</Table.Th>
                <Table.Th>Date</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {form.values.certifications.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={4}>
                    <Text c="dimmed" size="sm" ta="center" py="lg">
                      No certificates yet. Click Add Certificate to add one.
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ) : (
                form.values.certifications.map((cert) => (
                  <Table.Tr
                    key={cert.id}
                    style={{ cursor: 'pointer' }}
                    onClick={() => openCertEdit(cert)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openCertEdit(cert);
                      }
                    }}
                  >
                    <Table.Td><Text size="sm">{cert.boardCertificationName}</Text></Table.Td>
                    <Table.Td><Text size="sm">{cert.certificationNumber || '—'}</Text></Table.Td>
                    <Table.Td>
                      <Text size="sm">
                        {cert.certificationDate ? formatDateForDisplay(cert.certificationDate) : '—'}
                      </Text>
                    </Table.Td>
                    <Table.Td onClick={(e) => e.stopPropagation()}>
                      <ActionIcon variant="subtle" color="red" aria-label="Delete certificate" onClick={(e) => removeCertificate(cert.id, e)}>
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Table.Td>
                  </Table.Tr>
                ))
              )}
            </Table.Tbody>
          </Table>
        </div>

        {/* ===== DEA Credentials ===== */}
        <div>
          <Group justify="space-between" mb="xs">
            <Text size="sm" fw={600} c="gray.8">
              DEA credentials
            </Text>
            <Button leftSection={<IconPlus size={16} />} variant="default" size="xs" onClick={openDeaAdd}>
              Add DEA Credential
            </Button>
          </Group>
          <Table highlightOnHover withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>DEA Number Name</Table.Th>
                <Table.Th>Active</Table.Th>
                <Table.Th>Expiration Date</Table.Th>
                <Table.Th>Drug Schedules</Table.Th>
                <Table.Th>Business Activity</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {form.values.deaCredentials.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={6}>
                    <Text c="dimmed" size="sm" ta="center" py="lg">
                      No DEA credentials yet. Click Add DEA Credential to add one.
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ) : (
                form.values.deaCredentials.map((dea) => (
                  <Table.Tr
                    key={dea.id}
                    style={{ cursor: 'pointer' }}
                    onClick={() => openDeaEdit(dea)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openDeaEdit(dea);
                      }
                    }}
                  >
                    <Table.Td><Text size="sm">{dea.deaNumberName}</Text></Table.Td>
                    <Table.Td>
                      <Badge color={dea.active ? 'green' : 'gray'} size="sm" variant="light">
                        {dea.active ? 'Active' : 'Inactive'}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">
                        {dea.expirationDate ? formatDateForDisplay(dea.expirationDate) : '—'}
                      </Text>
                    </Table.Td>
                    <Table.Td><Text size="sm">{dea.drugSchedules || '—'}</Text></Table.Td>
                    <Table.Td><Text size="sm">{dea.businessActivity || '—'}</Text></Table.Td>
                    <Table.Td onClick={(e) => e.stopPropagation()}>
                      <ActionIcon variant="subtle" color="red" aria-label="Delete DEA credential" onClick={(e) => removeDea(dea.id, e)}>
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Table.Td>
                  </Table.Tr>
                ))
              )}
            </Table.Tbody>
          </Table>
        </div>

        {/* ===== Specialty Training ===== */}
        <div>
          <Group justify="space-between" mb="xs">
            <Text size="sm" fw={600} c="gray.8">
              Specialty training
            </Text>
            <Button leftSection={<IconPlus size={16} />} variant="default" size="xs" onClick={openSuppAdd}>
              Add Training
            </Button>
          </Group>
          <Table highlightOnHover withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Training Name</Table.Th>
                <Table.Th>Completed Date</Table.Th>
                <Table.Th>Training Type</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {form.values.specialtyTraining.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={4}>
                    <Text c="dimmed" size="sm" ta="center" py="lg">
                      No specialty training yet. Click Add Training to add one.
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ) : (
                form.values.specialtyTraining.map((cred) => (
                  <Table.Tr
                    key={cred.id}
                    style={{ cursor: 'pointer' }}
                    onClick={() => openSuppEdit(cred)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openSuppEdit(cred);
                      }
                    }}
                  >
                    <Table.Td><Text size="sm">{cred.trainingName}</Text></Table.Td>
                    <Table.Td>
                      <Text size="sm">
                        {cred.completedDate ? formatDateForDisplay(cred.completedDate) : '—'}
                      </Text>
                    </Table.Td>
                    <Table.Td><Text size="sm">{cred.trainingType || '—'}</Text></Table.Td>
                    <Table.Td onClick={(e) => e.stopPropagation()}>
                      <ActionIcon variant="subtle" color="red" aria-label="Delete training" onClick={(e) => removeSupp(cred.id, e)}>
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Table.Td>
                  </Table.Tr>
                ))
              )}
            </Table.Tbody>
          </Table>
        </div>

        <Divider />

        {/* ===== Completed Trainings (certifications) ===== */}
        <div>
          <Group justify="space-between" mb="xs">
            <Text size="sm" fw={600} c="gray.8">
              Completed trainings
            </Text>
            <Button leftSection={<IconPlus size={16} />} variant="default" size="xs" onClick={openTrainingAdd}>
              Add Training
            </Button>
          </Group>
          <Text size="xs" c="dimmed" mb="xs">
            Telehealth, law &amp; ethics, supervisory CE, and other required trainings.
          </Text>
          <Table highlightOnHover withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Training Type</Table.Th>
                <Table.Th>Completed Date</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {form.values.trainings.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={3}>
                    <Text c="dimmed" size="sm" ta="center" py="lg">
                      No trainings yet. Click Add Training to add one.
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ) : (
                form.values.trainings.map((training) => (
                  <Table.Tr key={training.id}>
                    <Table.Td><Text size="sm">{training.trainingType}</Text></Table.Td>
                    <Table.Td>
                      <Text size="sm">
                        {training.completedDate ? formatDateForDisplay(training.completedDate) : '—'}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <ActionIcon variant="subtle" color="red" aria-label="Delete training" onClick={(e) => removeTraining(training.id, e)}>
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Table.Td>
                  </Table.Tr>
                ))
              )}
            </Table.Tbody>
          </Table>
        </div>

        <Divider />

        {/* ===== CAQH Credentialing ===== */}
        <div>
          <Text size="sm" fw={600} c="gray.8" mb="xs">
            CAQH credentials
          </Text>
          <Grid>
            <Grid.Col span={{ base: 12, sm: 4 }}>
              <TextInput
                label="CAQH username"
                placeholder="CAQH username"
                withAsterisk
                {...form.getInputProps('caqhUsername')}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 4 }}>
              <PasswordInput
                label="CAQH password"
                placeholder="CAQH password"
                withAsterisk
                {...form.getInputProps('caqhPassword')}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 4 }}>
              <TextInput
                label="CAQH ID"
                placeholder="CAQH ID"
                withAsterisk
                {...form.getInputProps('caqhId')}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 4 }}>
              <UnifyDateInput
                label="Last CAQH attestation date"
                placeholder="Select a date"
                value={form.values.caqhAttestationDate}
                onChange={(value) => form.setFieldValue('caqhAttestationDate', value)}
                error={form.errors.caqhAttestationDate}
                leftSection={<IconCalendarEvent size={16} />}
                clearable
              />
            </Grid.Col>
          </Grid>
        </div>
      </Stack>

      {/* ===== Modals ===== */}

      {/* Education Modal */}
      <Modal
        title={editingEduId ? 'Edit Education' : 'New Education'}
        opened={eduModalOpen}
        onClose={() => { setEduModalOpen(false); setEditingEduId(null); }}
        size="lg"
      >
        <Stack gap="md">
          <TextInput
            label="Institution / University"
            placeholder="e.g. University of California, Los Angeles"
            {...educationModalForm.getInputProps('institution')}
          />
          <TextInput
            label="Degree"
            placeholder="e.g. PsyD, PhD, MSW, MA"
            {...educationModalForm.getInputProps('degree')}
          />
          <TextInput
            label="Graduation Year"
            placeholder="e.g. 2018"
            inputMode="numeric"
            value={educationModalForm.values.graduationYear}
            onChange={(e) =>
              educationModalForm.setFieldValue(
                'graduationYear',
                e.currentTarget.value.replace(/\D/g, '').slice(0, 4),
              )
            }
            error={educationModalForm.errors.graduationYear}
          />
        </Stack>
        <Group justify="flex-end" gap="sm" mt="xl">
          <Button variant="default" onClick={() => { setEduModalOpen(false); setEditingEduId(null); }}>Cancel</Button>
          <Button onClick={() => saveEducation()}>{editingEduId ? 'Save changes' : 'Add Education'}</Button>
        </Group>
      </Modal>

      {/* License Modal */}
      <Modal
        title={editingId ? 'Edit License' : 'New License'}
        opened={showModal}
        onClose={closeLicenseModal}
        size="lg"
      >
        <Stack gap="md">
          <Text size="sm" fw={600}>Summary</Text>
          <Grid>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <TextInput
                label="License Name"
                placeholder="License name"
                withAsterisk
                {...licenseModalForm.getInputProps('licenseName')}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <TextInput
                label="License Number"
                placeholder="License number"
                {...licenseModalForm.getInputProps('licenseNumber')}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <Select
                label="Jurisdiction State"
                placeholder="--None--"
                data={US_STATES}
                value={licenseModalForm.values.jurisdictionState}
                onChange={(v) => licenseModalForm.setFieldValue('jurisdictionState', v)}
                error={licenseModalForm.errors.jurisdictionState}
                clearable
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <Select
                label="License Status"
                placeholder="Select status"
                data={LICENSE_STATUS_OPTIONS}
                value={licenseModalForm.values.licenseStatus || null}
                onChange={(v) => licenseModalForm.setFieldValue('licenseStatus', v ?? '')}
                error={licenseModalForm.errors.licenseStatus}
                clearable
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <UnifyDateInput
                label="Issue Date"
                placeholder="Select a date"
                value={licenseModalForm.values.issueDate}
                onChange={(v) => licenseModalForm.setFieldValue('issueDate', v)}
                error={licenseModalForm.errors.issueDate}
                leftSection={<IconCalendarEvent size={16} />}
                clearable
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <UnifyDateInput
                label="Expiration Date"
                placeholder="Select a date"
                value={licenseModalForm.values.expirationDate}
                onChange={(v) => licenseModalForm.setFieldValue('expirationDate', v)}
                error={licenseModalForm.errors.expirationDate}
                leftSection={<IconCalendarEvent size={16} />}
                clearable
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <Select
                label="License Type"
                placeholder="--None--"
                data={LICENSE_TYPES}
                value={licenseModalForm.values.licenseType}
                onChange={(v) => licenseModalForm.setFieldValue('licenseType', v)}
                error={licenseModalForm.errors.licenseType}
                clearable
              />
            </Grid.Col>
            <Grid.Col span={12}>
              <Checkbox
                label="This is my primary active license"
                {...licenseModalForm.getInputProps('isPrimary', { type: 'checkbox' })}
              />
            </Grid.Col>
            <Grid.Col span={12}>
              <Checkbox
                label="This license has been superseded by a newer license"
                {...licenseModalForm.getInputProps('superseded', { type: 'checkbox' })}
              />
            </Grid.Col>
          </Grid>
          <div>
            <Text size="sm" fw={500} mb={4} component="label" display="block">
              License document (optional)
            </Text>
            <input
              ref={licenseDocInputRef}
              type="file"
              accept="application/pdf,image/*"
              style={{ display: 'none' }}
              onChange={(e) => licenseModalForm.setFieldValue('documentFile', e.target.files?.[0] ?? null)}
            />
            <Paper
              p="md"
              withBorder
              style={{ border: '2px dashed var(--mantine-color-gray-4)', textAlign: 'center', cursor: 'pointer' }}
              onClick={() => licenseDocInputRef.current?.click()}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); licenseDocInputRef.current?.click(); } }}
              aria-label="Click to upload license document"
            >
              <Text size="sm" c="dimmed">
                {licenseModalForm.values.documentFile
                  ? licenseModalForm.values.documentFile.name
                  : 'Drop file here or click to upload (PDF, image)'}
              </Text>
            </Paper>
          </div>
        </Stack>
        <Group justify="flex-end" gap="sm" mt="xl">
          <Button variant="default" onClick={closeLicenseModal}>Cancel</Button>
          <Button onClick={() => saveLicense()}>{editingId ? 'Save changes' : 'Add License'}</Button>
        </Group>
      </Modal>

      {/* Certificate Modal */}
      <Modal
        title={editingCertId ? 'Edit Certificate' : 'New Certificate'}
        opened={certModalOpen}
        onClose={() => { setCertModalOpen(false); setEditingCertId(null); }}
        size="lg"
      >
        <Stack gap="md">
          <TextInput
            label="Board Certification Name"
            placeholder="e.g. American Board of Professional Psychology"
            withAsterisk
            {...boardCertModalForm.getInputProps('boardCertificationName')}
          />
          <Select
            label="Certification Type"
            placeholder="Select type"
            data={BOARD_CERT_TYPE_OPTIONS}
            value={boardCertModalForm.values.certType}
            onChange={(v) => boardCertModalForm.setFieldValue('certType', v)}
            error={boardCertModalForm.errors.certType}
            clearable
          />
          <TextInput
            label="Certification Number"
            placeholder="Certification number"
            {...boardCertModalForm.getInputProps('certificationNumber')}
          />
          <UnifyDateInput
            label="Certification Date"
            placeholder="Select a date"
            value={boardCertModalForm.values.certificationDate}
            onChange={(v) => boardCertModalForm.setFieldValue('certificationDate', v)}
            error={boardCertModalForm.errors.certificationDate}
            leftSection={<IconCalendarEvent size={16} />}
            clearable
          />
          <div>
            <Text size="sm" fw={500} mb={4} component="label" display="block">
              Certificate document (optional)
            </Text>
            <input
              ref={certFileInputRef}
              type="file"
              accept="application/pdf,image/*"
              style={{ display: 'none' }}
              onChange={(e) => boardCertModalForm.setFieldValue('certificateFile', e.target.files?.[0] ?? null)}
            />
            <Paper
              p="md"
              withBorder
              style={{ border: '2px dashed var(--mantine-color-gray-4)', textAlign: 'center', cursor: 'pointer' }}
              onClick={() => certFileInputRef.current?.click()}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); certFileInputRef.current?.click(); } }}
              aria-label="Click to upload certificate file"
            >
              <Text size="sm" c="dimmed">
                {boardCertModalForm.values.certificateFile
                  ? boardCertModalForm.values.certificateFile.name
                  : 'Drop file here or click to upload (PDF, image)'}
              </Text>
            </Paper>
          </div>
        </Stack>
        <Group justify="flex-end" gap="sm" mt="xl">
          <Button variant="default" onClick={() => { setCertModalOpen(false); setEditingCertId(null); }}>Cancel</Button>
          <Button onClick={() => saveCertificate()}>{editingCertId ? 'Save changes' : 'Add Certificate'}</Button>
        </Group>
      </Modal>

      {/* DEA Modal */}
      <Modal
        title={editingDeaId ? 'Edit DEA Credential' : 'New DEA Credential'}
        opened={deaModalOpen}
        onClose={() => { setDeaModalOpen(false); setEditingDeaId(null); }}
        size="lg"
      >
        <Stack gap="md">
          <TextInput
            label="DEA Number Name"
            placeholder="DEA number name"
            withAsterisk
            {...deaModalForm.getInputProps('deaNumberName')}
          />
          <Checkbox label="Active" {...deaModalForm.getInputProps('active', { type: 'checkbox' })} />
          <UnifyDateInput
            label="Expiration Date"
            placeholder="Select a date"
            value={deaModalForm.values.expirationDate}
            onChange={(v) => deaModalForm.setFieldValue('expirationDate', v)}
            error={deaModalForm.errors.expirationDate}
            leftSection={<IconCalendarEvent size={16} />}
            clearable
          />
          <TextInput
            label="Drug Schedules"
            placeholder="e.g. Schedule II, III"
            {...deaModalForm.getInputProps('drugSchedules')}
          />
          <TextInput
            label="Business Activity"
            placeholder="Business activity"
            {...deaModalForm.getInputProps('businessActivity')}
          />
        </Stack>
        <Group justify="flex-end" gap="sm" mt="xl">
          <Button variant="default" onClick={() => { setDeaModalOpen(false); setEditingDeaId(null); }}>Cancel</Button>
          <Button onClick={() => saveDea()}>{editingDeaId ? 'Save changes' : 'Add DEA Credential'}</Button>
        </Group>
      </Modal>

      {/* Specialty Training Modal */}
      <Modal
        title={editingSuppId ? 'Edit Specialty Training' : 'New Specialty Training'}
        opened={suppModalOpen}
        onClose={() => { setSuppModalOpen(false); setEditingSuppId(null); }}
        size="lg"
      >
        <Stack gap="md">
          <TextInput
            label="Specialty Training Name"
            placeholder="e.g. Child and Adolescent Psychology"
            withAsterisk
            {...supplementalModalForm.getInputProps('trainingName')}
          />
          <UnifyDateInput
            label="Completed Date"
            placeholder="Select a date"
            value={supplementalModalForm.values.completedDate}
            onChange={(v) => supplementalModalForm.setFieldValue('completedDate', v)}
            error={supplementalModalForm.errors.completedDate}
            leftSection={<IconCalendarEvent size={16} />}
            clearable
          />
          <Select
            label="Training Type"
            placeholder="Select type"
            data={SPECIALTY_TRAINING_TYPE_OPTIONS}
            value={supplementalModalForm.values.trainingType}
            onChange={(v) => supplementalModalForm.setFieldValue('trainingType', v)}
            error={supplementalModalForm.errors.trainingType}
            clearable
          />
        </Stack>
        <Group justify="flex-end" gap="sm" mt="xl">
          <Button variant="default" onClick={() => { setSuppModalOpen(false); setEditingSuppId(null); }}>Cancel</Button>
          <Button onClick={() => saveSupp()}>{editingSuppId ? 'Save changes' : 'Add Training'}</Button>
        </Group>
      </Modal>

      {/* Completed Training Modal */}
      <Modal title="New Training" opened={trainingModalOpen} onClose={() => setTrainingModalOpen(false)} size="md">
        <Stack gap="md">
          <Select
            label="Training Type"
            placeholder="Select training type"
            data={TRAINING_SELECT_OPTIONS}
            value={trainingModalForm.values.trainingType || null}
            onChange={(v) => trainingModalForm.setFieldValue('trainingType', v ?? '')}
            error={trainingModalForm.errors.trainingType}
            withAsterisk
          />
          <UnifyDateInput
            label="Completed Date"
            placeholder="Select a date"
            value={trainingModalForm.values.completedDate}
            onChange={(v) => trainingModalForm.setFieldValue('completedDate', v)}
            leftSection={<IconCalendarEvent size={16} />}
            error={trainingModalForm.errors.completedDate}
            withAsterisk
            clearable
          />
        </Stack>
        <Group justify="flex-end" gap="sm" mt="xl">
          <Button variant="default" onClick={() => setTrainingModalOpen(false)}>Cancel</Button>
          <Button onClick={() => saveTraining()}>Add Training</Button>
        </Group>
      </Modal>
    </OnboardingSection>
  );
}
